<script setup lang="ts">
import type { ICurrency, IProvider, IWallet } from "@/user-account/types/UserAccount.type"
import { fetchBillingProviders, fetchBillingsCurrencies, fetchWallets } from "@/app/api"
import { useCurrencies } from "@/app/composables/useCurrencies"
import { t } from "@/app/composables/useI18n"
import { useUserService } from "@/user-account/composables/useUserService"
import { computed, onMounted, ref } from "vue"
import ClipLoader from "vue-spinner/src/ClipLoader.vue"
import SelectItem from "../../SelectItem.vue"
import DepositCrypto from "./DepositCrypto.vue"
import DepositDummy from "./DepositDummy.vue"
import DepositFiat from "./DepositFiat.vue"
import WithdrawCrypto from "./WithdrawCrypto.vue"
import WithdrawDummy from "./WithdrawDummy.vue"

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
})

const { fundWallets, activeWallet } = useUserService()
const { getIconByTicker } = useCurrencies()
const allowedCurrencies = ref<ICurrency[]>([])
const loading = ref(false)
const selectedCurrency = ref<ICurrency | Record<string, never>>({})
const providers = ref<IProvider[]>([])
const selectedProvider = ref<IProvider | null>(null)
const isDropdownOpen = ref(false)
const isProviderDropdownOpen = ref(false)
const userWallets = ref<IWallet[]>([])
const selectedWallet = ref<IWallet | null>(null)
const filteredWallets = computed(() => {
  if (selectedProvider.value?.alias === "coins-paid") {
    return fundWallets.value.filter(wallet => wallet.currencyType === "crypto")
  }

  return fundWallets.value
})

const actionNames = {
  deposit: "deposit",
  withdraw: "withdrawal",
}

async function loadAllowedCurrencies() {
  allowedCurrencies.value = []

  loading.value = true

  const params = {
    type: actionNames[props.type],
  }

  const { data } = await fetchBillingsCurrencies(params)
  allowedCurrencies.value = data.data || []
  loading.value = false
  selectedCurrency.value = activeWallet.value.currency
  if (allowedCurrencies.value.length > 0) {
    loadProviders(allowedCurrencies.value[0].id)
  }
}

async function loadProviders(currencyId: number) {
  providers.value = []

  const params = {
    currency_id: currencyId,
    type: actionNames[props.type],
  }

  loading.value = true
  const { data } = await fetchBillingProviders(params)
  providers.value = data.data || []
  loading.value = false
  selectedProvider.value = providers.value[0]
}

async function loadUserWallets() {
  try {
    loading.value = true

    const { data } = await fetchWallets()

    const wallets = data.data || []
    userWallets.value = wallets

    if (selectedCurrency.value?.id) {
      const matchingWallet = wallets.find(
        (wallet: IWallet) => wallet.currency.id === selectedCurrency.value.id && wallet.type === "funds",
      )

      if (matchingWallet) {
        selectedWallet.value = matchingWallet
      }
    }
  }
  catch (error) {
    console.error("Failed to load wallets:", error)
  }
  finally {
    loading.value = false
  }
}

function selectCurrency(currency: ICurrency) {
  selectedCurrency.value = currency

  const matchingWallet = userWallets.value.find(
    wallet => wallet.currency.id === currency.id && wallet.type === "funds",
  )

  if (matchingWallet) {
    selectedWallet.value = matchingWallet
  }

  loadProviders(currency.id)
}

function handleToggle(value: boolean) {
  isDropdownOpen.value = value
}

function handleProviderToggle(value: boolean) {
  isProviderDropdownOpen.value = value
}

function selectProvider(provider: IProvider) {
  selectedProvider.value = provider
}

onMounted(() => {
  if (activeWallet.value) {
    selectedCurrency.value = activeWallet.value.currency
  }
  loadAllowedCurrencies()
  loadUserWallets()
})
</script>

<template>
  <div v-if="allowedCurrencies.length > 0">
    <div v-if="providers.length > 1" class="label">
      {{ t("finance.select_billing") }}
    </div>
    <div v-if="providers.length > 1" class="select-item">
      <SelectItem
        :value="selectedProvider.name" :is-open="isProviderDropdownOpen"
        @toggle="handleProviderToggle"
      >
        <template #header>
          <div
            v-for="provider in providers"
            :key="provider.id"
            @click="selectProvider(provider)"
          >
            {{ provider.name }}
          </div>
        </template>
      </SelectItem>
    </div>

    <div class="label">
      {{ t("finance.select_currency") }}
    </div>
    <div class="select-item">
      <SelectItem
        :value="selectedCurrency.name"
        :icon="selectedCurrency?.ticker"
        :is-open="isDropdownOpen"
        @toggle="handleToggle"
      >
        <template #header>
          <div
            v-for="currency in filteredWallets"
            :key="currency?.currency?.id"
            @click="selectCurrency(currency?.currency)"
          >
            <div class="currency-balance">
              <img
                :src="getIconByTicker(currency?.currency?.ticker)"
                class="currency-icon"
                alt=""
              >
              {{ currency?.currency?.name }}
            </div>
          </div>
        </template>
      </SelectItem>
    </div>

    <DepositFiat
      v-if="
        props.type === 'deposit' && ['sentinal'].includes(selectedProvider?.alias)
      "
      :currency-id="(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
    <DepositCrypto
      v-else-if="props.type === 'deposit' && ['coins-paid'].includes(selectedProvider?.alias)"
      :currency-id="(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
    <DepositDummy
      v-else-if="props.type === 'deposit' && ['dummy'].includes(selectedProvider?.alias)"
      :currency-id="(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
    <WithdrawCrypto
      v-else-if="props.type === 'withdraw' && ['coins-paid'].includes(selectedProvider?.alias)"
      :currency-id="(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
      :wallet="selectedWallet"
    />
    <WithdrawDummy
      v-else-if="props.type === 'withdraw' && ['dummy'].includes(selectedProvider?.alias)"
      :currency-id="(selectedCurrency.id)"
      :provider-id="parseInt(selectedProvider.id)"
    />
  </div>

  <ClipLoader v-if="loading" :loading="true" color="white" size="20px" />

  <div v-if="providers.length === 0 && !loading">
    <span v-if="props.type === 'deposit'">{{ t("finance.deposit_unsupported") }}</span>
    <span v-else>{{ t("finance.withdraw_unsupported") }}</span>
  </div>

  <div v-if="allowedCurrencies.length === 0 && !loading">
    <span v-if="props.type === 'deposit'">{{ t("finance.deposit-not-allowed") }}</span>
    <span v-else>{{ t("finance.withdrawals_not_posible_message") }}</span>
  </div>
</template>

<style lang="scss" scoped>
.btn-g {
  color: var(--be-primary-btn-label);
  border: none;
  height: 42px;
  width: 100%;
  border-radius: 4px;
  background: var(--Linear, linear-gradient(90deg, #3886ec 0%, #544fe5 100%));
  margin: 12px 0px;
}

.currency-balance {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.currency-icon {
  width: 16px;
  height: 21px;
}

.withdraw-fee {
  font-size: 12px;
}

.label {
  margin-bottom: 7px;
}

.select-item {
  margin-bottom: 10px;
  height: 40px;
}

.deposit-amount {
  font-size: 14px;
  border-radius: 4px;
  background: var(--be-small-card-bg);
  border: none;
  height: 40px;
  outline: none;
  width: 100%;
  color: white;
  text-indent: 10px;
}

.min-deposit {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
}

.qr-code {
  display: flex;
  padding: 16px;
  border-radius: 4px;
  background: var(--be-small-card-bg);
  gap: 0 16px;
}
</style>
